import React from "react";
import {Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleIcon from "@mui/icons-material/Article";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import get from "lodash.get";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
// pdf.worker.entry exports nothing - it bundles the worker and hangs it on window.pdfjsWorker,
// which pdf.js reads ahead of GlobalWorkerOptions.workerSrc and then never asks for a worker
// URL. Same wiring the existing PDFViewer uses, so no worker file has to be served separately.
// The trade: parsing runs on the main thread, so a very large PDF costs a beat on load.
import "pdfjs-dist/build/pdf.worker.entry";

/**
 * Every document on the claim, merged into one continuous scroll.
 *
 * The claim's documents arrive as a list of presigned URLs. Rather than a tab per document -
 * each with its own inner scroll - every PDF page is rendered to a canvas and every image
 * inlined, all stacked in a single scrolling column, so the whole file set reads top to
 * bottom in one gesture while the tariff decision is taken beside it.
 *
 * There is no viewer toolbar: pages are always fitted to the width of the column, which is
 * the only setting a reading pane beside a table actually needs. Anything else - zooming,
 * rotating a sideways scan, printing - is one click away per document, in the browser's own
 * viewer, from the actions on that document's header strip.
 *
 * Pages render lazily as they come near the viewport and are sized from their real viewport
 * beforehand, so the scrollbar is honest before anything has painted. A document pdf.js
 * cannot fetch (a bucket that does not allow cross-origin reads) falls back to an iframe,
 * which keeps its own scroll but still shows the file.
 */

// Rendered a little before it is scrolled to, so scrolling rarely lands on a blank page.
const RENDER_MARGIN = '800px';
const PAGE_GUTTER = 32;

/**
 * The reading column is the height of the screen, stated against the viewport rather than
 * inherited: the panel it sits in is fixed to the full height of the window, so a percentage
 * would only add a chain of ancestors that all have to keep their own height for this one to
 * survive.
 */
const VIEWER_HEIGHT = '100vh';

// A document pdf.js could not fetch is shown in its own frame - one screen of it, less the
// document's header strip and the column's padding, so it fills the pane like the rest.
const IFRAME_HEIGHT = 'calc(100vh - 62px)';

const isPdf = (name) => /\.pdf(\?|$)/i.test(String(name || ''));
const isImage = (name) => /\.(png|jpe?g|gif|webp|bmp|tiff?)(\?|$)/i.test(String(name || ''));

const fileNameOf = (doc, index) => String(get(doc, ['fileName'], `Document ${index + 1}`) || `Document ${index + 1}`);
const urlOf = (doc) => String(get(doc, ['preSingedURL'], '') || '');

/**
 * One PDF page. It holds a placeholder of the right size until it comes near the viewport,
 * then paints itself at the device's pixel ratio so the text stays sharp.
 */
const PdfPage = ({pdf, pageNumber, baseWidth, baseHeight, scale, label}) => {
    const holderRef = React.useRef(null);
    const canvasRef = React.useRef(null);
    const taskRef = React.useRef(null);
    const [near, setNear] = React.useState(false);
    const [painted, setPainted] = React.useState(false);

    const width = Math.round(baseWidth * scale);
    const height = Math.round(baseHeight * scale);

    React.useEffect(() => {
        const holder = holderRef.current;
        if (!holder || near) {
            return undefined;
        }
        if (typeof IntersectionObserver !== 'function') {
            setNear(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setNear(true);
                observer.disconnect();
            }
        }, {rootMargin: RENDER_MARGIN});
        observer.observe(holder);
        return () => observer.disconnect();
    }, [near]);

    React.useEffect(() => {
        if (!near || !pdf) {
            return undefined;
        }
        let cancelled = false;
        setPainted(false);

        const paint = async () => {
            try {
                const page = await pdf.getPage(pageNumber);
                if (cancelled) {
                    return;
                }
                const viewport = page.getViewport({scale});
                const canvas = canvasRef.current;
                if (!canvas) {
                    return;
                }
                const ratio = Math.min(window.devicePixelRatio || 1, 2);
                canvas.width = Math.floor(viewport.width * ratio);
                canvas.height = Math.floor(viewport.height * ratio);
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;

                taskRef.current = page.render({
                    canvasContext: canvas.getContext('2d'),
                    viewport,
                    transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
                });
                await taskRef.current.promise;
                if (!cancelled) {
                    setPainted(true);
                }
            } catch (e) {
                // A cancelled render is the expected outcome of the column being resized mid-paint.
            }
        };

        paint();
        return () => {
            cancelled = true;
            if (taskRef.current) {
                taskRef.current.cancel();
                taskRef.current = null;
            }
        };
    }, [near, pdf, pageNumber, scale]);

    return (
        <Box
            ref={holderRef}
            style={{
                width: `${width}px`,
                height: `${height}px`,
                margin: '0 auto 12px',
                background: '#ffffff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <canvas ref={canvasRef} style={{display: painted ? 'block' : 'none'}}/>
            {!painted && (
                <Box style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9e9e9e',
                    fontSize: '0.75rem',
                    gap: '8px',
                }}>
                    <CircularProgress size={16}/> {label}
                </Box>
            )}
        </Box>
    );
};

/** A row of actions that apply to one document, in the browser's own viewer. */
const DocumentActions = ({url, name}) => (
    <Box style={{display: 'flex', alignItems: 'center', gap: '2px'}}>
        <Tooltip title="Open in a new tab - zoom, rotate and search there">
            <IconButton size="small" disabled={!url} onClick={() => window.open(url, '_blank', 'noopener')}>
                <OpenInNewIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
        <Tooltip title="Open to print">
            <IconButton size="small" disabled={!url} onClick={() => {
                const opened = window.open(url, '_blank', 'noopener');
                if (opened) {
                    opened.addEventListener?.('load', () => opened.print?.());
                }
            }}>
                <PrintIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
        <Tooltip title={`Download ${name}`}>
            <IconButton size="small" disabled={!url} onClick={() => window.open(url, '_blank', 'noopener')}>
                <DownloadIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    </Box>
);

/**
 * One document in the stack: a PDF rendered page by page, an image inlined, anything else
 * offered for download.
 */
const DocumentBlock = ({doc, index, containerWidth}) => {
    const url = urlOf(doc);
    const name = fileNameOf(doc, index);
    const [pdf, setPdf] = React.useState(null);
    const [pages, setPages] = React.useState([]);
    const [loading, setLoading] = React.useState(isPdf(name));
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        if (!isPdf(name) || !url) {
            return undefined;
        }
        let cancelled = false;
        let loaded = null;
        setLoading(true);
        setFailed(false);

        const load = async () => {
            try {
                const task = pdfjsLib.getDocument({url, isEvalSupported: false});
                loaded = await task.promise;
                if (cancelled) {
                    loaded.destroy();
                    return;
                }
                // Real page sizes up front, so the scrollbar is honest before anything paints.
                const sizes = [];
                for (let pageNumber = 1; pageNumber <= loaded.numPages; pageNumber += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const page = await loaded.getPage(pageNumber);
                    const viewport = page.getViewport({scale: 1});
                    sizes.push({pageNumber, width: viewport.width, height: viewport.height});
                }
                if (cancelled) {
                    loaded.destroy();
                    return;
                }
                setPdf(loaded);
                setPages(sizes);
                setLoading(false);
            } catch (e) {
                if (!cancelled) {
                    // Most often a bucket that will not allow a cross-origin read - the iframe
                    // fallback below still shows the file.
                    setFailed(true);
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
            if (loaded) {
                loaded.destroy();
            }
        };
    }, [url, name]);

    /** Always fitted to the column: the page's own width against the room available. */
    const scaleFor = (page) => {
        if (!(page.width > 0) || !(containerWidth > 0)) {
            return 1;
        }
        return Math.max((containerWidth - PAGE_GUTTER) / page.width, 0.1);
    };

    return (
        <Box style={{marginBottom: '22px'}}>
            <Box style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                padding: '6px 10px',
                background: '#eceff1',
                borderRadius: '4px',
                position: 'sticky',
                top: 0,
                zIndex: 2,
            }}>
                <Box style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0}}>
                    <ArticleIcon fontSize="small" style={{color: '#546e7a'}}/>
                    <Box style={{minWidth: 0}}>
                        <Typography style={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                            {index + 1}. {name}
                        </Typography>
                        <Typography style={{fontSize: '0.65rem', color: '#607d8b'}}>
                            {get(doc, ['stage'], '') || 'Document'}
                            {pages.length > 0 && ` · ${pages.length} page(s)`}
                            {failed && ' · shown in its own frame'}
                        </Typography>
                    </Box>
                </Box>
                <DocumentActions url={url} name={name}/>
            </Box>

            {loading && (
                <Box style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '18px', color: '#757575'}}>
                    <CircularProgress size={18}/>
                    <Typography style={{fontSize: '0.78rem'}}>Loading {name}…</Typography>
                </Box>
            )}

            {!loading && isPdf(name) && !failed && pages.map((page) => (
                <PdfPage
                    key={page.pageNumber}
                    pdf={pdf}
                    pageNumber={page.pageNumber}
                    baseWidth={page.width}
                    baseHeight={page.height}
                    scale={scaleFor(page)}
                    label={`Page ${page.pageNumber}`}
                />
            ))}

            {!loading && isPdf(name) && failed && (
                <iframe
                    title={name}
                    src={url}
                    style={{
                        width: '100%',
                        height: IFRAME_HEIGHT,
                        border: '1px solid #cfd8dc',
                        background: '#ffffff',
                    }}
                />
            )}

            {isImage(name) && (
                <Box style={{textAlign: 'center', marginTop: '8px'}}>
                    <img
                        src={url}
                        alt={name}
                        style={{
                            width: '100%',
                            maxWidth: '100%',
                            background: '#ffffff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                        }}
                    />
                </Box>
            )}

            {!isPdf(name) && !isImage(name) && (
                <Paper variant="outlined" style={{padding: '18px', textAlign: 'center', marginTop: '8px'}}>
                    <ErrorOutlineIcon style={{color: '#b0bec5'}}/>
                    <Typography style={{fontSize: '0.78rem', marginBottom: '8px'}}>
                        {name} cannot be shown inline.
                    </Typography>
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small"/>}
                            disabled={!url} onClick={() => window.open(url, '_blank', 'noopener')}>
                        Download file
                    </Button>
                </Paper>
            )}
        </Box>
    );
};

function MergedDocumentViewer(props) {
    const {claimId, files} = props;

    const [documents, setDocuments] = React.useState(() => (Array.isArray(files) ? files : []));
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [reloadToken, setReloadToken] = React.useState(0);
    const [containerWidth, setContainerWidth] = React.useState(0);

    const scrollRef = React.useRef(null);

    // The document list is the same one the Documents tab reads.
    React.useEffect(() => {
        if (Array.isArray(files) && files.length > 0) {
            setDocuments(files.filter((doc) => urlOf(doc)));
            setLoading(false);
            return undefined;
        }
        if (!claimId) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError('');

        axios({
            url: `/document/list/${claimId}`,
            baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/su/support`,
            method: "GET",
            headers: {
                "X-Client-Platform": "CLAIM-ENHANCEMENT",
                "Content-Type": "blob",
                "X-Auth-Token": `${localStorage.getItem("authToken")}`,
            },
        })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const listed = get(response, ['data'], []);
                setDocuments((Array.isArray(listed) ? listed : []).filter((doc) => urlOf(doc)));
                setLoading(false);
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e?.message || 'Could not load the claim documents');
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [claimId, files, reloadToken]);

    // Fitting a page to the column needs to know how much room the column actually has.
    React.useEffect(() => {
        const node = scrollRef.current;
        if (!node) {
            return undefined;
        }
        const measure = () => setContainerWidth(node.clientWidth);
        measure();
        if (typeof ResizeObserver !== 'function') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [loading]);

    return (
        <Box
            ref={scrollRef}
            style={{
                height: VIEWER_HEIGHT,
                boxSizing: 'border-box',
                overflow: 'auto',
                padding: '10px',
                background: '#f5f5f5',
                minWidth: 0,
            }}
        >
            {loading && (
                <Box style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '24px'}}>
                    <CircularProgress size={20}/>
                    <Typography style={{fontSize: '0.82rem'}}>Loading the claim documents…</Typography>
                </Box>
            )}

            {!loading && error !== '' && (
                <Paper variant="outlined" style={{padding: '18px', textAlign: 'center'}}>
                    <Typography style={{fontSize: '0.82rem', color: '#c62828'}}>{error}</Typography>
                    <Button size="small" style={{marginTop: '8px'}} startIcon={<RefreshIcon fontSize="small"/>}
                            onClick={() => setReloadToken((prev) => prev + 1)}>
                        Try again
                    </Button>
                </Paper>
            )}

            {!loading && error === '' && documents.length === 0 && (
                <Typography style={{fontSize: '0.82rem', color: '#757575', padding: '24px', textAlign: 'center'}}>
                    No document is present for this claim.
                </Typography>
            )}

            {!loading && error === '' && documents.map((doc, index) => (
                <DocumentBlock
                    key={`${fileNameOf(doc, index)}-${index}`}
                    doc={doc}
                    index={index}
                    containerWidth={containerWidth}
                />
            ))}
        </Box>
    );
}

export default MergedDocumentViewer;
